import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import { ExportMenu, DateRangeFilter, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';
import './LeadsList.scss';

const EMPTY_FILTERS = {
  status: 'all', search: '', teamSize: '', industry: '', preferredTime: '',
  from: '', to: '', slotFrom: '', slotTo: '',
};

// Submitted range → ISO day boundaries; the preferred-slot range stays "YYYY-MM-DD"
// because preferredDate is stored as that string.
const toApiParams = ({ from, to, ...rest }) => ({ ...rest, ...dateRangeParams(from, to) });

const pickBookings = (body) => ({
  rows: body.data?.bookings || [],
  totalPages: body.data?.pagination?.totalPages,
});

const TIME_SLOT_LABELS = {
  morning: 'Morning · 10:00–12:00',
  afternoon: 'Afternoon · 12:00–15:00',
  evening: 'Evening · 15:00–18:00'
};

// Build a compact list of page numbers + "..." ellipses for pagination.
// Always shows first and last page, current page ±1, with ellipses for gaps.
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const DemoBookingsList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, page: 1, limit: 20 });
  const [viewingBooking, setViewingBooking] = useState(null);
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  const filtersActive = Object.keys(EMPTY_FILTERS).some((k) => filters[k] !== EMPTY_FILTERS[k]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['demo-bookings', filters],
    queryFn: () => superAdminAPI.getDemoBookings(toApiParams(filters))
  });

  const bookings = data?.data?.data?.bookings || [];
  const pagination = data?.data?.data?.pagination || {};
  const facets = data?.data?.data?.facets || {};

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => superAdminAPI.updateDemoBookingStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['demo-bookings']);
    }
  });

  const getStatusBadge = (status) => {
    const classes = {
      pending: 'badge-warning',
      confirmed: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return classes[status] || 'badge-secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      confirmed: '📅',
      completed: '✅',
      cancelled: '❌'
    };
    return icons[status] || '📋';
  };

  // LeadsList.scss colours the modal badge by status; 'confirmed' reuses 'booked'
  const modalStatusClass = (status) => (status === 'confirmed' ? 'booked' : status);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatPreferredDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const slotLabel = (id) => TIME_SLOT_LABELS[id] || id;

  const handleStatusUpdate = (bookingId, newStatus) => {
    if (window.confirm(`Are you sure you want to mark this demo booking as ${newStatus}?`)) {
      updateStatusMutation.mutate({ id: bookingId, status: newStatus });
    }
  };

  return (
    <div className="leads-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Demo Bookings</h1>
          <p>Demo requests submitted from the landing page Book a Demo form</p>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search by name, email, company, mobile, message..."
          value={filters.search}
          onChange={(e) => setFilter({ search: e.target.value })}
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={(e) => setFilter({ status: e.target.value })}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filters.teamSize}
          onChange={(e) => setFilter({ teamSize: e.target.value })}
          className="filter-select"
        >
          <option value="">All Team Sizes</option>
          {(facets.teamSizes || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filters.industry}
          onChange={(e) => setFilter({ industry: e.target.value })}
          className="filter-select"
        >
          <option value="">All Industries</option>
          {(facets.industries || []).map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select
          value={filters.preferredTime}
          onChange={(e) => setFilter({ preferredTime: e.target.value })}
          className="filter-select"
        >
          <option value="">All Time Slots</option>
          {Object.entries(TIME_SLOT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <DateRangeFilter
          label="Submitted"
          from={filters.from}
          to={filters.to}
          onChange={({ from, to }) => setFilter({ from, to })}
        />
        <DateRangeFilter
          label="Demo date"
          from={filters.slotFrom}
          to={filters.slotTo}
          onChange={({ from, to }) => setFilter({ slotFrom: from, slotTo: to })}
        />
        <ResetFiltersButton
          active={filtersActive}
          onReset={() => setFilter(EMPTY_FILTERS)}
        />
        <ExportMenu
          fileName="demo-bookings"
          getRows={() => fetchAllPages(superAdminAPI.getDemoBookings, toApiParams(filters), { pick: pickBookings })}
          columns={[
            { header: 'Name', value: (b) => b.fullName },
            { header: 'Company', value: (b) => b.company },
            { header: 'Email', value: (b) => b.workEmail },
            { header: 'Mobile', value: (b) => b.mobile },
            { header: 'Team Size', value: (b) => b.teamSize },
            { header: 'Industry', value: (b) => b.industry },
            { header: 'Preferred Date', value: (b) => b.preferredDate },
            { header: 'Preferred Time', value: (b) => slotLabel(b.preferredTime) },
            { header: 'Status', value: (b) => b.status },
            { header: 'Message', value: (b) => b.message },
            { header: 'Source', value: (b) => b.source },
            { header: 'Submitted', value: (b) => formatExportDate(b.createdAt) },
          ]}
        />
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading demo bookings...</div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Error loading demo bookings</h3>
            <p>{error?.message || 'Failed to fetch demo bookings. Please check if the backend is running.'}</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No demo bookings found</h3>
            <p>Bookings from the Book a Demo form will appear here</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Team Size</th>
                  <th>Industry</th>
                  <th>Preferred Slot</th>
                  <th>Status</th>
                  <th>Submitted At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking._id}>
                    <td>
                      <div className="name-cell">
                        <span className="name">{booking.fullName}</span>
                      </div>
                    </td>
                    <td>{booking.company}</td>
                    <td>
                      <a href={`mailto:${booking.workEmail}`} className="email-link">
                        {booking.workEmail}
                      </a>
                    </td>
                    <td>
                      <a href={`tel:${booking.mobile}`} className="phone-link">
                        {booking.mobile}
                      </a>
                    </td>
                    <td>
                      <span className="badge badge-info">{booking.teamSize}</span>
                    </td>
                    <td>{booking.industry}</td>
                    <td>
                      <div className="submission-info">
                        <span className="submission-date">
                          {formatPreferredDate(booking.preferredDate)}
                        </span>
                        <span className="submission-time">
                          {slotLabel(booking.preferredTime)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td>
                      <div className="submission-info">
                        <span className="submission-date">
                          {new Date(booking.createdAt).toLocaleDateString()}
                        </span>
                        <span className="submission-time">
                          {formatTime(booking.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setViewingBooking(booking)}
                        >
                          View
                        </button>
                        {booking.status === 'confirmed' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleStatusUpdate(booking._id, 'completed')}
                            disabled={updateStatusMutation.isPending}
                          >
                            Complete
                          </button>
                        )}
                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
                            disabled={updateStatusMutation.isPending}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(pagination.totalPages > 1 || pagination.total > 0) && (
        <div className="pagination">
          <div className="pagination-info">
            <span className="page-info">
              Showing {((pagination.page - 1) * filters.limit) + 1}–{Math.min(pagination.page * filters.limit, pagination.total)} of {pagination.total}
            </span>
            <div className="rows-per-page">
              <label htmlFor="demo-rows-per-page">Rows per page:</label>
              <select
                id="demo-rows-per-page"
                value={filters.limit}
                onChange={(e) => setFilters(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                className="filter-select filter-select--sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-outline btn-sm"
                disabled={filters.page === 1}
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <div className="page-buttons">
                {buildPageList(filters.page, pagination.totalPages).map((item, idx) =>
                  item === '...' ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`btn btn-page${filters.page === item ? ' active' : ''}`}
                      onClick={() => setFilters(prev => ({ ...prev, page: item }))}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                disabled={filters.page === pagination.totalPages}
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* View Demo Booking Modal */}
      {viewingBooking && (
        <div className="lead-modal-overlay" onClick={() => setViewingBooking(null)}>
          <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="lead-modal-header">
              <div className="header-content">
                <div className="lead-avatar">
                  {viewingBooking.fullName?.charAt(0)?.toUpperCase() || 'D'}
                </div>
                <div className="header-info">
                  <h2>{viewingBooking.fullName}</h2>
                  <p className="company-name">{viewingBooking.company}</p>
                </div>
              </div>
              <div className="header-status">
                <span className={`status-badge ${modalStatusClass(viewingBooking.status)}`}>
                  {getStatusIcon(viewingBooking.status)} {viewingBooking.status}
                </span>
              </div>
              <button className="modal-close-btn" onClick={() => setViewingBooking(null)}>
                ×
              </button>
            </div>

            {/* Body */}
            <div className="lead-modal-body">
              {/* Quick Actions */}
              <div className="quick-actions">
                <a href={`mailto:${viewingBooking.workEmail}`} className="quick-action-btn email">
                  <span className="icon">📧</span>
                  <span>Send Email</span>
                </a>
                <a href={`tel:${viewingBooking.mobile}`} className="quick-action-btn phone">
                  <span className="icon">📞</span>
                  <span>Call</span>
                </a>
                <button
                  className="quick-action-btn copy"
                  onClick={() => {
                    navigator.clipboard.writeText(viewingBooking.workEmail);
                    alert('Email copied!');
                  }}
                >
                  <span className="icon">📋</span>
                  <span>Copy Email</span>
                </button>
              </div>

              {/* Contact Information */}
              <div className="info-section">
                <h3>
                  <span className="section-icon">👤</span>
                  Contact Information
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Full Name</span>
                    <span className="value">{viewingBooking.fullName}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Work Email</span>
                    <a href={`mailto:${viewingBooking.workEmail}`} className="value link">
                      {viewingBooking.workEmail}
                    </a>
                  </div>
                  <div className="info-item">
                    <span className="label">Mobile Number</span>
                    <a href={`tel:${viewingBooking.mobile}`} className="value link">
                      {viewingBooking.mobile}
                    </a>
                  </div>
                  <div className="info-item">
                    <span className="label">Company</span>
                    <span className="value">{viewingBooking.company}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Team Size</span>
                    <span className="value team-size">{viewingBooking.teamSize}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Industry</span>
                    <span className="value">{viewingBooking.industry}</span>
                  </div>
                </div>
              </div>

              {/* Preferred Slot */}
              <div className="info-section">
                <h3>
                  <span className="section-icon">📅</span>
                  Preferred Slot (IST)
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Date</span>
                    <span className="value">{formatPreferredDate(viewingBooking.preferredDate)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Time Slot</span>
                    <span className="value">{slotLabel(viewingBooking.preferredTime)}</span>
                  </div>
                </div>
              </div>

              {/* Message */}
              {viewingBooking.message && (
                <div className="info-section">
                  <h3>
                    <span className="section-icon">💬</span>
                    Message
                  </h3>
                  <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{viewingBooking.message}</p>
                </div>
              )}

              {/* Timeline */}
              <div className="info-section">
                <h3>
                  <span className="section-icon">⏱️</span>
                  Timeline
                </h3>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-dot created"></div>
                    <div className="timeline-content">
                      <span className="timeline-label">Booking Submitted</span>
                      <span className="timeline-time">{formatDateTime(viewingBooking.createdAt)}</span>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-dot updated"></div>
                    <div className="timeline-content">
                      <span className="timeline-label">Last Updated</span>
                      <span className="timeline-time">{formatDateTime(viewingBooking.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="lead-modal-footer">
              <button className="btn btn-outline" onClick={() => setViewingBooking(null)}>
                Close
              </button>
              {viewingBooking.status === 'pending' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleStatusUpdate(viewingBooking._id, 'confirmed');
                    setViewingBooking(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Mark as Confirmed
                </button>
              )}
              {viewingBooking.status === 'confirmed' && (
                <button
                  className="btn btn-success"
                  onClick={() => {
                    handleStatusUpdate(viewingBooking._id, 'completed');
                    setViewingBooking(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  ✓ Mark as Completed
                </button>
              )}
              {(viewingBooking.status === 'pending' || viewingBooking.status === 'confirmed') && (
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    handleStatusUpdate(viewingBooking._id, 'cancelled');
                    setViewingBooking(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Cancel Booking
                </button>
              )}
              {viewingBooking.status === 'cancelled' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleStatusUpdate(viewingBooking._id, 'pending');
                    setViewingBooking(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Reactivate
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoBookingsList;
